import type { ProposalOrderStatus } from '@moment/horizon-data-access/proposal'

import { Badge, BadgeVariant } from '@moment/design-system-ui/badge'
import { useMemo } from 'react'

import { AdvisorViewStatus } from '@/types/enums/advisor-view-status'
import { TraderViewStatus } from '@/types/enums/trader-view-status'
import {
  isActionRequired,
  isTerminalStatus,
  type TradeBlotterOrderStatus,
} from '@/utils/trade-blotter/compute-order-status'

interface OrderStatusBadgeProps {
  status: TradeBlotterOrderStatus | ProposalOrderStatus
  className?: string
}

export function formatStatus(
  status: TradeBlotterOrderStatus | ProposalOrderStatus,
) {
  return status
    .split('_')
    .map((word, index) => {
      if (word.toUpperCase() === 'RFQ') return 'RFQ'
      return index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase()
    })
    .join(' ')
}

export function OrderStatusBadge({
  status,
  className = '',
}: OrderStatusBadgeProps) {
  const variant = useMemo(() => {
    if (isActionRequired(status as TradeBlotterOrderStatus)) {
      return BadgeVariant.Warning
    }
    if (!isTerminalStatus(status as TradeBlotterOrderStatus)) {
      return BadgeVariant.Processing
    }
    const isFilled =
      status === TraderViewStatus.Filled || status === AdvisorViewStatus.Filled
    if (isFilled) {
      return BadgeVariant.Success
    }
    const isRejected =
      status === TraderViewStatus.Rejected ||
      status === AdvisorViewStatus.Rejected
    if (isRejected) {
      return BadgeVariant.Danger
    }
    const isCanceled =
      status === TraderViewStatus.Canceled ||
      status === AdvisorViewStatus.Canceled
    if (isCanceled) {
      return BadgeVariant.Neutral
    }
    return BadgeVariant.Neutral
  }, [status])

  return (
    <Badge className={`capitalize ${className}`} variant={variant}>
      {formatStatus(status)}
    </Badge>
  )
}
